import axios from 'axios';
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Flex, FormLabel, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Text, Toast, useDisclosure } from "@chakra-ui/react";

import { useApiClient } from "../../shared/hooks";
import { TextEditor } from '../../features/admin/blogs/components';

interface IBlog{
  title:string,
  description:string,
  content:string,
  cover_image:string,
  author:string,
  country_id:string
  updated_at:string
  gs_country:{
      name:string
  }
}

interface IBloggedPlaces{
  id:string,
  name:string,
  created_at:string,
  updated_at:string
}

function CreateBlogPage() {
  const [bloggedPlaces, setBloggedPlaces] = useState<IBloggedPlaces[]>([]);
  const [searchImage, setSearchImage] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(searchImage);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imageData, setImageData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isOpen, onClose } = useDisclosure();
  const { register, control, setValue, handleSubmit, reset } = useForm<IBlog>();

  const apiClient = useApiClient();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Debounce Logic integrated
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchImage);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchImage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.getBlogCategories();
        setBloggedPlaces(response.data as any);
      } catch (error) {
        console.log(error, "Error during Fetch blogged places");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setImageData([]);
        // const data = await imagedData(debouncedSearch);
        // setImageData(data.data);
        setIsLoading(false);
      } catch (error) {
        console.log(error, "Error during Fetch Image from Unsplash");
      }
    };
    if (debouncedSearch) {
      fetchData();
    }
  }, [debouncedSearch]);

  const uploadImage = async (image: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('file', image);
      formData.append('type', 'blogs');

      let baseUrl = 'https://f8jcwt5745.execute-api.us-east-1.amazonaws.com/dev';
      if (localStorage.getItem("backend_env") === "prod") {
        baseUrl = 'https://vwt190bfa2.execute-api.us-east-1.amazonaws.com/prod';
      } else if (localStorage.getItem("backend_env") === "stag") {
        baseUrl = 'https://hp9zzz7tf8.execute-api.us-east-1.amazonaws.com/stag';
      }

      const accessToken = JSON.parse(localStorage.getItem("user")!).access_token;

      // Use upload image form tips feature
      const response = await axios.post(
        `${baseUrl}/tips/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      return response.data.data.Location;
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrorMessage("Error uploading image");
      throw error;
    }
  };

  const submit = async (data: IBlog) => {
    let response;
    try {
      setIsLoading(true);

      // Upload cover image if exists
      if (coverImage) {
        const coverImageUrl = await uploadImage(coverImage);
        data.cover_image = coverImageUrl;
      } else {
        setErrorMessage("Cover Image is required.");
        setIsLoading(false);
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
        return;
      }

      response = await apiClient.createBlog(data);

      if (response.status) {
        setIsLoading(false);
        setSuccessMessage("Blog submitted successfully!");
        localStorage.removeItem("content");
        reset();
        setTimeout(() => {
          setSelectedImage("");
          setSuccessMessage(null);
          setIsLoading(false);
        }, 6000);
      } else {
        setErrorMessage(response.message || "Error submitting blog");
        setIsLoading(false);
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      }
    } catch (error: any) {
      console.log(error, "ERROR");
      setErrorMessage(response?.message ?? "Error submitting blog uncaught");
      setIsLoading(false);
      setTimeout(() => {
        setErrorMessage(null);
      }, 2000);
    }
  };

  return (
    <form>
      <Flex
        flexDirection={"column"}
        alignItems={"flex-start"}
        marginTop={{ base: "30px", md: "50px" }}
        gap={{ base: "10px", md: "20px" }}
        paddingX={{ base: "20px", md: "40px" }}
      >
        <Flex
          flexDirection={{ base: "column", sm: "row" }}
          width={"100%"}
          gap={{ base: "20px", sm: "40px" }}
          alignItems={{ base: "start", sm: "end" }}
        >
          <Flex flexDirection={"column"}>
            {selectedImage && (
              <Box
                overflow={"hidden"}
                marginBottom={{ base: "10px", sm: "20px" }}
              >
                <Image
                  src={selectedImage}
                  objectFit="cover"
                  height={"60"}
                  width={"60"}
                />
              </Box>
            )}
            <Flex direction="column" align="center">
              <Button onClick={() => document.getElementById('coverImageInput')?.click()}>
                {coverImage ? coverImage.name : 'Upload Cover Image'}
              </Button>
              <Input
                id="coverImageInput"
                type="file"
                display="none"
                onChange={(e) => setCoverImage(e.target.files ? e.target.files[0] : null)}
              />
            </Flex>
            <Box>
              <Modal
                blockScrollOnMount={true}
                isOpen={isOpen}
                onClose={onClose}
                scrollBehavior="inside"
              >
                <ModalOverlay />
                <ModalContent minW={"70vw"}>
                  <ModalHeader fontSize={"1.5em"}>
                    <Box
                      width={"80%"}
                      maxW={"500px"}
                      gap={"20px"}
                      marginTop={{ base: "5px", sm: "10px" }}
                    >
                      <Box>
                        <FormLabel>Search Image</FormLabel>
                        <Input
                          placeholder="Enter your text..."
                          _placeholder={{
                            opacity: 1,
                            color: "#878999",
                          }}
                          onChange={(e) => setSearchImage(e.target.value)}
                        />
                      </Box>
                      <Box>
                        <FormLabel>Image URL</FormLabel>
                        <Input
                          placeholder="Paste Image URL..."
                          _placeholder={{
                            opacity: 1,
                            color: "#878999",
                          }}
                          onChange={(e) =>{
                            setSelectedImage(e.target.value);
                            setValue("cover_image",e.target.value);
                          }}
                        />
                      </Box>
                    </Box>
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody
                    minH="fit-content"
                    overflowY="auto"
                    overflowX={"hidden"}
                  >
                    <Flex flexDirection="row" gap="20px" wrap={"wrap"}>
                      {isLoading && (
                        <Spinner
                          thickness="4px"
                          speed="0.65s"
                          emptyColor="gray.200"
                          color="blue.500"
                          size="md"
                          marginRight={"100px"}
                        />
                      )}
                      {imageData.results &&
                        imageData.results.map((element: any) => (
                          <Image
                            key={element.id}
                            src={element.urls.small}
                            alt={`Image-${element.id}`}
                            height="60%"
                            style={{
                              cursor: "pointer",
                              border:
                                selectedImage === element.urls.small
                                  ? "2px solid blue"
                                  : "none",
                            }}
                            onClick={() => {
                              setSelectedImage(element.urls.small);
                              setValue("cover_image", element.urls.small);
                            }}
                          />
                        ))}
                    </Flex>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      width={"150px"}
                      bg="#4286F5"
                      color="#FFFFFF"
                      mr={3}
                      onClick={onClose}
                    >
                      Done
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </Box>
          </Flex>
          <Box width={"60"}>
            <Select
              placeholder="Select Category"
              color="#363B52"
              id="categoryID"
              {...register("country_id")}
            >
              {bloggedPlaces.map((element) => (
                <option key={element.id} value={element.id}>
                  {element.name.charAt(0).toUpperCase() +
                    element.name.slice(1)}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
        <Flex
          width={"100%"}
          flexDirection={{ base: "column", sm: "row" }}
          gap={{ base: "15px", sm: "30px" }}
          alignItems={{ base: "start", sm: "end" }}
        >
          <Flex
            width={"80%"}
            maxWidth={"400px"}
            flexDirection={"column"}
            gap={"0.5em"}
          >
            <Text fontSize={"1em"}>Title</Text>
            <Input
              placeholder="Enter title of Blog"
              _placeholder={{ opacity: 1, color: "#878999" }}
              id="title"
              fontSize={"1em"}
              height={50}
              {...register("title")}
            />
          </Flex>
          <Flex
            width={"100%"}
            maxWidth={"650px"}
            flexDirection={"column"}
            gap={"0.5em"}
          >
            <Text fontSize={"1em"}>Description</Text>
            <Input
              placeholder="Enter Description around 200 word ..."
              _placeholder={{ opacity: 1, color: "#878999" }}
              height={50}
              fontSize={"1em"}
              id="description"
              {...register("description")}
            />
          </Flex>
        </Flex>
        <Box
          marginTop={{ base: "10px", md: "20px" }}
          width={"100%"}
          marginBottom={"50px"}
        >
          <TextEditor
            name="content"
            control={control}
            defaultValue={localStorage.getItem("content")}
            setValue={setValue}
          />
          <Button
            bg="#4286F5"
            color="#FFFFFF"
            fontSize={"1em"}
            fontWeight={500}
            width={"200px"}
            isLoading={isLoading}
            loadingText={"submitting"}
            style={{ marginTop: "20px" }}
            onClick={handleSubmit(submit)}
          >
            Publish
          </Button>
        </Box>

        <Box position="fixed" top="5" left="50%" transform="translateX(-50%)">
          {successMessage && (
            <Toast
              title={`${successMessage} `}
              status="success"
              duration={5000}
              isClosable={true}
            />
          )}
          {isLoading && (
            <Flex
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="50vh"
            >
              <Spinner color="orange.500" />
            </Flex>
          )}
        </Box>
        {errorMessage && (
          <Box
            position="fixed"
            top="5"
            left="50%"
            transform="translateX(-50%)"
          >
            <Toast
              title={`${errorMessage}`}
              status="error"
              duration={2000}
              isClosable={true}
            />
          </Box>
        )}
      </Flex>
    </form>
  );
}

export default CreateBlogPage;
